"use client";

import {
  // Compass,
  Plus,
  ArrowLeft,
  LogIn,
  LogOut,
  Loader2,
  User,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import WorkspaceListModal from "@/app/components/WorkspaceListModal";
import { WorkspaceSelectModal } from "@/app/components/WorkspaceSelectModal";
import { WorkspaceSidebarList } from "@/app/components/WorkspaceSidebarList";
import { useElementWidth } from "@/hooks/useElementWidth";
import { useWorkerLayout } from "@/hooks/useWorkerLayout";
import { useStickyModalHost } from "@/hooks/useStickyModalHost";
import { useSearchParams } from "next/navigation";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";

import WorkerSidebar from "@/app/components/WorkerSidebar";
import SearchInput from "@/app/components/SearchInput";
import { useOrdersContext } from "@/context/OrdersContext";
import DiscoverContent from "@/app/components/DiscoverContent";
import Logo from "@/app/components/Logo";

import { DynamicSetupModal } from "@/app/components/onboarding/DynamicSetupModal";
import { ResponseModal } from "@/app/components/onboarding/ResponseModal";
import ShareModal from "@/app/components/ShareModal";
import FilePreviewModal from "@/app/components/FilePreviewModal";
import Modal from "@/app/components/ui/Modal";
import HireModal from "@/app/components/HireModal";
import { HowAeraWorksSection } from "@/app/components/HowAeraWorksSection";
import { useCart } from "@/context/CartContext";
import ServiceDetailInline from "@/app/components/ServiceDetailInline";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { navigateToSignin } from "@/lib/openLineExternalBrowser";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import {
  buildHomeHrefAfterWorkspacePick,
  continuePendingBillinGo,
} from "@/lib/billinGoRoutes";
import {
  persistLastWorkspace,
  readLastWorkspace,
} from "@/lib/lastWorkspaceStorage";
import { showWorkspaceSwitchToast } from "@/lib/workspaceSwitchToast";
import { openCookieConsentBanner } from "@/lib/cookieConsent";
import {
  checkServiceHiredInWorkspace,
  resolveWorkspacePickHint,
} from "@/lib/workspacePickHint";

const HIRE_AERA_SECTION_NAV = [
  { id: "hire-workers", label: "AI Workers ของเรา" }, // Our Workers
  { id: "hire-how-it-works", label: "Aera ทำงานอย่างไร" }, // How Aera Works
  { id: "hire-benefits", label: "ข้อดีของ AI Workers" },
  { id: "hire-credits", label: "เครดิต & แพ็กเกจ" },
  { id: "hire-faq", label: "คำถามที่พบบ่อย" },
] as const;

function DiscoverSidebarSkeleton() {
  return (
    <div className="p-4 lg:p-6 h-full overflow-y-auto animate-pulse">
      <div className="h-8 w-32 rounded bg-aera-lightgray mb-8 mx-2" />
      <nav className="space-y-1" aria-hidden="true">
        {Array.from({ length: HIRE_AERA_SECTION_NAV.length }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2">
            <div className="w-2.5 h-2.5 shrink-0 rounded-full bg-aera-lightgray" />
            <div
              className="h-6 rounded bg-aera-lightgray"
              style={{ width: `${120 + i * 14}px` }}
            />
          </div>
        ))}
      </nav>
    </div>
  );
}

function HomeContent() {
  const searchParams = useSearchParams();

  const currentWorkspaceId = searchParams?.get("workspaceId") || null;
  const wantsSelectWorkspace = searchParams?.get("selectworkspace") === "true";
  const [isWorkspaceSelectModalOpen, setIsWorkspaceSelectModalOpen] =
    useState(false);
  const [workspacePickHintId, setWorkspacePickHintId] = useState<string | null>(
    null,
  );
  const [isWorkspacePickSubmitting, setIsWorkspacePickSubmitting] =
    useState(false);
  const [mounted, setMounted] = useState(false);
  const isEmbedded = searchParams?.get("embedded") === "true";
  const showDiscover =
    searchParams?.get("view") === "discover" ||
    (!searchParams?.get("view") &&
      !searchParams?.get("serviceId") &&
      !searchParams?.get("workspaceId"));

  // --- Logic from useWorkerLayout (Home) ---
  const {
    router,
    userAvatar,
    userDisplayName,
    isLoggedIn,
    isWorkspaceModalOpen,
    setIsWorkspaceModalOpen,
    workspaces,
    isLoadingWorkspaces,
    isLoadingSession,
    fetchWorkspaces,
    handleLogoutAction,
    userId,
  } = useWorkerLayout();
  const [workspaceModalMode, setWorkspaceModalMode] = useState<
    "create" | "edit"
  >("create");
  const { addToCart } = useCart();

  const {
    orders,
    marketplaceWorkers,
    loadingMarketplace,
    fetchOrders,
    fetchMarketplace,
    workersFromOrders,
  } = useOrdersContext();

  const patchUrlSearchParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      mutate(params);
      const qs = params.toString();
      router.replace(qs ? `/?${qs}` : "/", { scroll: false });
    },
    [router, searchParams],
  );

  const openWorkspaceSettingsModal = useCallback(() => {
    if (!currentWorkspaceId) return;
    setWorkspaceModalMode("edit");
    // เปิดจาก URL อย่างเดียว — ไม่ setIsWorkspaceModalOpen ทันที (กันเปิด→ปิด→เปิด ซ้ำตอน router ยังไม่อัปเดต)
    patchUrlSearchParams((params) => {
      params.set("workspaceId", currentWorkspaceId);
      params.set("setting", "true");
    });
  }, [currentWorkspaceId, patchUrlSearchParams]);

  const openCreateWorkspaceModal = useCallback(() => {
    patchUrlSearchParams((params) => {
      params.delete("setting");
    });
    setWorkspaceModalMode("create");
    setIsWorkspaceModalOpen(true);
  }, [patchUrlSearchParams, setIsWorkspaceModalOpen]);

  const closeWorkspaceModal = useCallback(() => {
    patchUrlSearchParams((params) => {
      params.delete("setting");
    });
    setIsWorkspaceModalOpen(false);
  }, [patchUrlSearchParams, setIsWorkspaceModalOpen]);

  const closeWorkspaceSelectModal = useCallback(() => {
    setIsWorkspaceSelectModalOpen(false);
    patchUrlSearchParams((params) => {
      params.delete("selectworkspace");
    });
  }, [patchUrlSearchParams]);

  const navigateAfterWorkspacePick = useCallback(
    async (workspaceId: string, opts?: { setting?: boolean }) => {
      setIsWorkspacePickSubmitting(true);
      try {
        if (workspaceId !== currentWorkspaceId) {
          const picked = workspaces.find((w) => w.id === workspaceId);
          showWorkspaceSwitchToast(picked?.name);
        }
        persistLastWorkspace(workspaceId);

        if (
          continuePendingBillinGo(workspaceId, (href) => {
            router.replace(href, { scroll: false });
          })
        ) {
          return;
        }

        const fromSelectWorkspace =
          searchParams?.get("selectworkspace") === "true";
        const sid =
          searchParams?.get("serviceId")?.trim() ||
          (fromSelectWorkspace ? BILLIN_SERVICE_ID : "");

        if (sid) {
          const isHired = await checkServiceHiredInWorkspace(workspaceId, sid);
          router.replace(
            buildHomeHrefAfterWorkspacePick(workspaceId, sid, {
              isHired,
              setting: opts?.setting,
            }),
            { scroll: false },
          );
          return;
        }

        patchUrlSearchParams((params) => {
          params.set("workspaceId", workspaceId);
          params.delete("selectworkspace");
        });
      } finally {
        setIsWorkspacePickSubmitting(false);
        setIsWorkspaceSelectModalOpen(false);
      }
    },
    [
      currentWorkspaceId,
      patchUrlSearchParams,
      router,
      searchParams,
      workspaces,
    ],
  );

  const dismissWorkspaceSelectModal = useCallback(() => {
    if (isWorkspacePickSubmitting) return;

    void (async () => {
      if (searchParams?.get("selectworkspace") !== "true") {
        closeWorkspaceSelectModal();
        return;
      }

      let targetId =
        workspacePickHintId?.trim() || currentWorkspaceId?.trim() || null;

      if (!targetId && workspaces.length > 0) {
        targetId = await resolveWorkspacePickHint(workspaces, {
          urlWorkspaceId: currentWorkspaceId,
        });
      }

      if (targetId) {
        await navigateAfterWorkspacePick(targetId);
        return;
      }

      closeWorkspaceSelectModal();
    })();
  }, [
    closeWorkspaceSelectModal,
    currentWorkspaceId,
    isWorkspacePickSubmitting,
    navigateAfterWorkspacePick,
    searchParams,
    workspacePickHintId,
    workspaces,
  ]);

  const handleWorkspaceSelectFromModal = useCallback(
    (workspaceId: string) => {
      if (isWorkspacePickSubmitting) return;
      void navigateAfterWorkspacePick(workspaceId);
    },
    [isWorkspacePickSubmitting, navigateAfterWorkspacePick],
  );

  useEffect(() => {
    if (currentWorkspaceId) {
      persistLastWorkspace(currentWorkspaceId);
    }
  }, [currentWorkspaceId]);

  const lastWorkspaceRestoreRef = useRef(false);

  // Logged-in home visit without ?workspaceId= — restore from `aera_last_workspace`.
  useEffect(() => {
    if (lastWorkspaceRestoreRef.current) return;
    if (currentWorkspaceId) return;
    if (wantsSelectWorkspace) return;
    if (isEmbedded) return;
    if (searchParams?.get("view") === "discover") return;
    if (searchParams?.get("serviceId")) return;
    if (!mounted || isLoadingSession || !isLoggedIn) return;
    if (isLoadingWorkspaces) return;

    const lastWid = readLastWorkspace()?.trim();
    if (!lastWid) {
      lastWorkspaceRestoreRef.current = true;
      return;
    }

    if (workspaces.length > 0 && !workspaces.some((w) => w.id === lastWid)) {
      lastWorkspaceRestoreRef.current = true;
      return;
    }

    lastWorkspaceRestoreRef.current = true;
    router.replace(`/?workspaceId=${encodeURIComponent(lastWid)}`, {
      scroll: false,
    });
  }, [
    currentWorkspaceId,
    wantsSelectWorkspace,
    isEmbedded,
    searchParams,
    mounted,
    isLoadingSession,
    isLoggedIn,
    isLoadingWorkspaces,
    workspaces,
    router,
  ]);

  useEffect(() => {
    setIsWorkspaceSelectModalOpen(wantsSelectWorkspace);
  }, [wantsSelectWorkspace]);

  useEffect(() => {
    if (!wantsSelectWorkspace || !isLoggedIn) return;
    void fetchOrders("buyer");
  }, [wantsSelectWorkspace, isLoggedIn, fetchOrders]);

  useEffect(() => {
    if (!wantsSelectWorkspace || isLoadingWorkspaces) {
      setWorkspacePickHintId(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      const hint = await resolveWorkspacePickHint(workspaces, {
        urlWorkspaceId: currentWorkspaceId,
      });
      if (!cancelled) setWorkspacePickHintId(hint);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    wantsSelectWorkspace,
    isLoadingWorkspaces,
    workspaces,
    currentWorkspaceId,
  ]);

  useEffect(() => {
    const wantsSettings = searchParams?.get("setting") === "true";
    const wsId = searchParams?.get("workspaceId");
    if (wantsSettings && wsId) {
      setWorkspaceModalMode("edit");
      setIsWorkspaceModalOpen(true);
      return;
    }
    if (workspaceModalMode === "edit") {
      setIsWorkspaceModalOpen(false);
    }
  }, [searchParams, workspaceModalMode, setIsWorkspaceModalOpen]);

  // --- Logic from ServiceIndexContent (Service Page) ---
  const serviceId = searchParams?.get("serviceId");
  const source = searchParams?.get("source");

  // Mobile Sidebar States
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Sidebar state
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarWidth, setSidebarWidth] = useState(288);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const [isLoginNavigationPending, setIsLoginNavigationPending] =
    useState(false);
  const [isDiscoverContentReady, setIsDiscoverContentReady] = useState(false);
  const [loadedSidebarWorkspaceId, setLoadedSidebarWorkspaceId] = useState<
    string | null
  >(currentWorkspaceId || null);
  const [menuClick, setMenuClick] = useState(!!serviceId);
  const [mobileServicePanelEntered, setMobileServicePanelEntered] =
    useState(false);

  // Login button sets pending before navigate; clear when auth lands (same/other tab) or user returns
  useEffect(() => {
    if (isLoggedIn) {
      setIsLoginNavigationPending(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const clearLoginPending = () => setIsLoginNavigationPending(false);

    const onVisible = () => {
      if (!document.hidden) clearLoginPending();
    };

    window.addEventListener("auth:login", clearLoginPending);
    window.addEventListener("auth:loginSuccess", clearLoginPending);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("auth:login", clearLoginPending);
      window.removeEventListener("auth:loginSuccess", clearLoginPending);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    setMenuClick(!!serviceId);
  }, [serviceId]);

  // Slide-in on mobile: start off-screen, then animate to open after paint
  useEffect(() => {
    if (!serviceId) {
      setMobileServicePanelEntered(false);
      return;
    }
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) {
      setMobileServicePanelEntered(true);
      return;
    }
    setMobileServicePanelEntered(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setMobileServicePanelEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [serviceId]);

  // Logic to determine if we should show the Detail View (Iframe/Placeholder) or the Group Logic (Discover List)
  const showServiceView = !!serviceId || !showDiscover;

  // Discover Data States (Removed - handled by DiscoverContent)
  // const [popularWorkers, setPopularWorkers] = useState<any[]>([]);
  // const [topRatedWorkers, setTopRatedWorkers] = useState<any[]>([]);
  // const [availableWorkers, setAvailableWorkers] = useState<any[]>([]);
  // const [loadingDiscover, setLoadingDiscover] = useState(true);

  const hiredWorkerIds = useMemo(
    () =>
      orders
        .filter((o) => {
          if (!currentWorkspaceId) return true;
          return (o.workspaceId || o.workspace?.id) === currentWorkspaceId;
        })
        .map((o) => o.serviceId),
    [orders, currentWorkspaceId],
  );

  // Use marketplace workers as API workers
  const apiWorkers = marketplaceWorkers;
  const isLoadingWorkers = loadingMarketplace;
  const isLoginButtonLoading =
    !mounted || isLoadingSession || isLoginNavigationPending;
  const isLoginButtonDisabled = isLoginButtonLoading || isLoggedIn;
  const showDiscoverSidebarSkeleton =
    showDiscover &&
    (!mounted ||
      isLoadingSession ||
      isLoginNavigationPending ||
      (!isLoggedIn && !isDiscoverContentReady));

  const activeTab =
    source === "order" || source === "my-team" ? "my-team" : "marketplace";

  const selectedWorker = useMemo(() => {
    if (!serviceId) return null;

    if (activeTab === "my-team") {
      return (
        workersFromOrders.find(
          (w) =>
            w.id === serviceId &&
            (!currentWorkspaceId || w.workspaceId === currentWorkspaceId),
        ) || null
      );
    }

    return marketplaceWorkers.find((w) => w.id === serviceId) || null;
  }, [
    serviceId,
    activeTab,
    workersFromOrders,
    marketplaceWorkers,
    currentWorkspaceId,
  ]);

  // Refetch data when workspace changes - use ref to prevent infinite loop
  const prevWorkspaceId = useRef<string | null>(null);
  useEffect(() => {
    // Only refetch if workspaceId actually changed
    // And if currentWorkspaceId is defined (or becomes null)
    const wsIdToUse = currentWorkspaceId || null;

    // We check if fetchMarketplace is needed based on showDiscover
    // But we always fetch orders if workspace changes
    if (prevWorkspaceId.current !== wsIdToUse) {
      prevWorkspaceId.current = wsIdToUse || null; // Store string or null

      void (async () => {
        try {
          if (wsIdToUse) {
            await Promise.all([
              fetchOrders("buyer", wsIdToUse),
              !showDiscover ? fetchMarketplace(wsIdToUse) : Promise.resolve(),
            ]);
          } else {
            await Promise.all([
              fetchOrders("buyer"),
              !showDiscover ? fetchMarketplace() : Promise.resolve(),
            ]);
          }
        } finally {
          setLoadedSidebarWorkspaceId(wsIdToUse);
        }
      })();
    } else if (!showDiscover) {
      // If workspace hasn't changed, but we switched AWAY from discover (or just loaded),
      // make sure marketplace data is loaded if it's not already
      // Note: OrdersContext handles caching, so calling this again is safe-ish,
      // but we rely on the context to not re-fetch if cached.
      // For now, let's stick to the workspace change trigger to avoid over-fetching
      // as per the requirement to stop "duplicate" calls.
    }
  }, [currentWorkspaceId, fetchOrders, fetchMarketplace, showDiscover]);

  const isSidebarWorkspaceLoading =
    !showDiscover && loadedSidebarWorkspaceId !== (currentWorkspaceId || null);

  // Fetch Discover Data (Removed - handled by DiscoverContent)

  // --- Consolidated Modal State ---
  const [activeModal, setActiveModal] = useState<
    "SETUP" | "RESPONSE" | "SHARE" | "PREVIEW" | "HIRE" | "HOW_IT_WORKS" | null
  >(null);
  const {
    displayed: displayedModal,
    isOpen: isActiveModalOpen,
    onExited: onActiveModalExited,
  } = useStickyModalHost(activeModal);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [modalProps, setModalProps] = useState<Record<string, any>>({});
  const [setupSubmitting, setSetupSubmitting] = useState(false);

  // Ref for bridging the submit promise
  const submitPromiseRef = useRef<{
    resolve: (val?: void) => void;
    reject: (err: unknown) => void;
  } | null>(null);

  // Show "How It Works" modal for signed-in users on landing (capped to once per 24 hours).
  // Public (logged-out) users do not get the auto-show; they can still open it manually
  // via the "วิธีการทำงาน" button in SearchInput.
  useEffect(() => {
    if (isLoadingSession) return;
    if (!isLoggedIn || !userId) return;
    if (serviceId || source) return;

    const key = `aera_last_how_it_works_seen_${userId}`;
    const lastStart = localStorage.getItem(key);
    const now = Date.now();

    if (!lastStart || now - parseInt(lastStart) > 24 * 60 * 60 * 1000) {
      setActiveModal("HOW_IT_WORKS");
    }
  }, [serviceId, source, isLoadingSession, userId, isLoggedIn]);

  // Listen for messages from iframe (Order creation & Modal control)
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (!data) return;

      if (data.type === "AERA_ORDER_CREATED") {
        const targetWorkspaceId =
          typeof data.workspaceId === "string" && data.workspaceId
            ? data.workspaceId
            : currentWorkspaceId || undefined;
        const targetServiceId =
          typeof data.serviceId === "string" && data.serviceId
            ? data.serviceId
            : searchParams?.get("serviceId");

        // Refresh orders to update My Team immediately
        await fetchOrders("buyer", targetWorkspaceId);

        // Update URL to reflect order status without refresh
        const params = new URLSearchParams(searchParams?.toString() || "");
        if (targetWorkspaceId) params.set("workspaceId", targetWorkspaceId);
        if (targetServiceId) params.set("serviceId", targetServiceId);
        params.set("source", "order");
        router.replace(`/?${params.toString()}`, { scroll: false });
      }

      // --- Remote Modal Handlers ---
      if (data.type === "OPEN_DYNAMIC_SETUP_MODAL") {
        const props = { ...data.props };

        // Auto-fill Business Setup if workspace name is stored
        if (props.stepTitle === "Business Setup") {
          const storedWorkspaceName = sessionStorage.getItem(
            "aera_setup_workspace_name",
          );

          if (storedWorkspaceName) {
            // Merge with existing initialValues if any
            props.initialValues = {
              ...props.initialValues,
              business_info: storedWorkspaceName,
            };
            props.autoSubmit = true;
            // DO NOT remove item here. It causes race conditions if this event fires multiple times.
            // Remove it on close instead.
          }
        }

        setModalProps(props);
        setActiveModal("SETUP");
      } else if (data.type === "CLOSE_DYNAMIC_SETUP_MODAL") {
        setActiveModal((prev) => {
          if (prev === "SETUP") {
            // Cleanup workspace name storage when flow completes/closes
            sessionStorage.removeItem("aera_setup_workspace_name");
            return null;
          }
          return prev;
        });
      } else if (data.type === "OPEN_RESPONSE_MODAL") {
        setModalProps(data.props);
        setActiveModal("RESPONSE");
      } else if (data.type === "CLOSE_RESPONSE_MODAL") {
        setActiveModal((prev) => (prev === "RESPONSE" ? null : prev));
      } else if (data.type === "OPEN_SHARE_MODAL") {
        setModalProps(data.props);
        setActiveModal("SHARE");
      } else if (data.type === "CLOSE_SHARE_MODAL") {
        setActiveModal((prev) => (prev === "SHARE" ? null : prev));
      } else if (data.type === "OPEN_FILE_PREVIEW_MODAL") {
        setModalProps(data.props);
        setActiveModal("PREVIEW");
      } else if (data.type === "OPEN_HIRE_MODAL") {
        setModalProps(data.props);
        setActiveModal("HIRE");
      } else if (data.type === "MODAL_SUBMIT_RESULT") {
        if (submitPromiseRef.current) {
          if (data.success) {
            submitPromiseRef.current.resolve(data.result);
          } else {
            submitPromiseRef.current.reject(data.error);
          }
          submitPromiseRef.current = null;
        }
      } else if (data.type === "CLOSE_SERVICE_VIEW") {
        if (data && typeof data === "object" && "targetUrl" in data) {
          const v = (data as { targetUrl?: unknown }).targetUrl;
          if (typeof v === "string" && v.trim().length > 0) {
            router.push(v);
            return;
          }
        }

        const params = new URLSearchParams(searchParams?.toString() || "");
        params.delete("serviceId");
        params.delete("source");
        router.push(`/?${params.toString()}`);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [currentWorkspaceId, fetchOrders, fetchMarketplace, router, searchParams]);

  // Debug Modal State
  useEffect(() => {}, [activeModal, modalProps]);

  // Handlers for Remote Modals

  const handleModalClose = () => {
    // Handle side effects based on active modal type
    if (activeModal === "SETUP") {
      window.postMessage(
        { type: "MODAL_CLOSED_BY_USER", modal: "dynamic_setup" },
        "*",
      );
    } else if (activeModal === "RESPONSE") {
      window.postMessage(
        { type: "MODAL_CLOSED_BY_USER", modal: "response" },
        "*",
      );
    }

    setActiveModal(null);
  };

  const handleRemoteReloadChat = () => {
    window.postMessage({ type: "RELOAD_CHAT_REQUEST" }, "*");
    setActiveModal(null);
  };

  // Legacy handlers removed or mapped to handleModalClose if used elsewhere
  // But they are only used in this file, so replacement is safe.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRemoteSetupSubmit = async (data: any) => {
    // ... existing logic ...
    // We need to keep this handler function available as it's passed to DynamicSetupModal
    return new Promise<void>((resolve, reject) => {
      submitPromiseRef.current = { resolve, reject };
      window.postMessage(
        {
          type: "MODAL_SUBMIT_REQUEST",
          payload: data,
        },
        "*",
      );
    });
  };

  // Load saved width from localStorage; track viewport for tablet sidebar width
  useEffect(() => {
    setMounted(true);
    const savedSidebarWidth = localStorage.getItem("worker_sidebar_width");
    if (savedSidebarWidth) setSidebarWidth(parseInt(savedSidebarWidth));

    const updateWindowWidth = () => setWindowWidth(window.innerWidth);
    updateWindowWidth();
    window.addEventListener("resize", updateWindowWidth);
    return () => window.removeEventListener("resize", updateWindowWidth);
  }, []);

  const isTabletViewport = mounted && windowWidth >= 768 && windowWidth < 1024;
  const effectiveSidebarWidth = isTabletViewport ? 300 : sidebarWidth;
  const { ref: mainPanelRef, width: mainPanelWidth } = useElementWidth();

  // Resizing logic
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizingSidebar) {
        let newWidth = e.clientX;

        if (newWidth < 260) {
          newWidth = 260;
        }

        if (newWidth > 360) {
          newWidth = 360;
        }
        setSidebarWidth(newWidth);
        localStorage.setItem("worker_sidebar_width", newWidth.toString());
      }
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
      document.body.style.cursor = "default";
    };

    if (isResizingSidebar) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingSidebar, showDiscover]);

  // const handleDiscoverClick = () => {
  //   // Clear workspace when going to Discover
  //   const params = new URLSearchParams();
  //   params.set("view", "discover");
  //   router.push(`/?${params.toString()}`);
  // };

  const handleMenuClick = (showMenu: boolean) => {
    setMenuClick(showMenu);
  };

  const hideHireSidebarOnMobile = showDiscover && !isLoggedIn;
  const hideWorkspaceRailOnMobile = !!serviceId;
  const mobileMainPanelOpen =
    hideHireSidebarOnMobile ||
    (serviceId ? mobileServicePanelEntered : menuClick);
  // Full-bleed as soon as the panel opens on mobile (before serviceId lands in the URL)
  const mobileFullWidthPanel =
    hideWorkspaceRailOnMobile || (mobileMainPanelOpen && showServiceView);

  const scrollToHireSection = (sectionId: string) => {
    const runScroll = () => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };
    if (
      typeof window !== "undefined" &&
      window.innerWidth < 768 &&
      !hideHireSidebarOnMobile
    ) {
      handleMenuClick(true);
      window.setTimeout(runScroll, 320);
      return;
    }
    runScroll();
  };

  return (
    <div className={`bg-aera-offwhite flex w-full`}>
      {/* Workspace Sidebar - hide in embedded mode */}
      {!isEmbedded && (
        <div
          className={`w-[63px] h-[calc(100dvh-0px)] fixed md:relative z-30 md:z-10 mt-0 shrink-0 grid grid-rows-[50px_auto_40px] gap-2 items-center bg-aera-offwhite py-2 ${mobileFullWidthPanel ? "" : ""}`}
        >
          {/* Logo + brand name */}
          <div className="flex flex-col items-center justify-start gap-1">
            <div className="flex items-center justify-center">
              <Logo
                width={28}
                height={28}
                classNameCrt="#000"
                classNameStr="#F59E0B"
              />
            </div>
            <div className="text-xs font-semibold text-aera-charcoal leading-none">
              Aera
            </div>
          </div>

          <div
            className={`flex min-h-0 flex-col w-full h-[calc(100dvh-122px)] items-center justify-start overflow-hidden ${workspaces.length > 0 ? "gap-2" : "gap-2"}`}
          >
            <>
              {/* Fixed Items: Discover (always visible) — temporarily hidden */}
              {/* {[
                {
                  key: "discover",
                  onClick: handleDiscoverClick,
                  isActive: showDiscover && !currentWorkspaceId,
                  renderIcon: (active: boolean) => (
                    <Compass
                      className={`${active ? "text-aera-white" : "text-aera-charcoal"}`}
                      size={24}
                      strokeWidth={2.5}
                    />
                  ),
                  Wrapper: "button" as const,
                },
              ].map(({ key, onClick, isActive, renderIcon, Wrapper }) => (
                <Wrapper
                  key={key}
                  onClick={onClick}
                  className={`w-10 min-w-10 h-10 min-h-10 relative rounded-xl flex items-center justify-center mx-0 md:mx-auto gap-2 cursor-pointer transition-all hover:scale-105 active:scale-95 shrink-0 ${isActive ? 'bg-aera-charcoal before:content-[""] before:absolute before:-left-3 before:rounded-br-lg before:rounded-tr-lg before:bg-aera-charcoal before:z-10 before:w-1.5 before:h-10' : "bg-aera-lightgray"}`}
                  title={key === "home" ? "Home" : "Discover"}
                >
                  {renderIcon(isActive || false)}
                </Wrapper>
              ))} */}

              {/* Workspaces + Add button only when logged in */}
              {mounted &&
                isLoggedIn &&
                (isLoadingWorkspaces ? (
                  <div
                    className={`flex flex-col gap-2 ${isLoadingWorkspaces && workspaces.length > 0 ? "mt-2" : ""}`}
                  >
                    {Array(3)
                      .fill(null)
                      .map((_, i) => (
                        <div
                          key={i}
                          className="w-10 h-10 rounded-xl bg-gray-200 animate-pulse mx-0 md:mx-auto shrink-0"
                        />
                      ))}
                  </div>
                ) : (
                  <>
                    {/* Scrollable Workspaces */}
                    {workspaces.length > 0 && (
                      <WorkspaceSidebarList
                        workspaces={workspaces}
                        highlightedWorkspaceId={
                          workspacePickHintId ?? currentWorkspaceId
                        }
                        onSelect={(workspace) => {
                          if (workspace.id !== currentWorkspaceId) {
                            showWorkspaceSwitchToast(workspace.name);
                          }
                          persistLastWorkspace(workspace.id);
                          if (
                            continuePendingBillinGo(workspace.id, (href) => {
                              router.replace(href, { scroll: false });
                            })
                          ) {
                            return;
                          }
                          const fromSelectWorkspace =
                            searchParams?.get("selectworkspace") === "true";
                          const sid =
                            searchParams?.get("serviceId")?.trim() ||
                            (fromSelectWorkspace ? BILLIN_SERVICE_ID : "");
                          if (sid) {
                            void (async () => {
                              const isHired =
                                await checkServiceHiredInWorkspace(
                                  workspace.id,
                                  sid,
                                );
                              router.replace(
                                buildHomeHrefAfterWorkspacePick(
                                  workspace.id,
                                  sid,
                                  { isHired },
                                ),
                                { scroll: false },
                              );
                            })();
                            return;
                          }
                          router.push(`/?workspaceId=${workspace.id}`);
                        }}
                      />
                    )}

                    {/* Fixed Add Workspace Button */}
                    <AeraButton
                      variant="lightgray"
                      paddingSize="none"
                      onClick={() => {
                        openCreateWorkspaceModal();
                      }}
                      className={`w-10 min-w-10 h-10 min-h-10 shrink-0 hover:scale-105 active:scale-95`}
                      title={
                        workspaces.length >= 1
                          ? "Workspace already created"
                          : "Add Workspace"
                      }
                    >
                      <Plus
                        className="text-aera-charcoal"
                        size={24}
                        strokeWidth={2.5}
                      />
                    </AeraButton>
                  </>
                ))}
            </>
          </div>

          <div className="w-10 h-10 mx-auto">
            {isLoginButtonLoading ? (
              <div
                className="relative w-10 h-10 flex items-center justify-center rounded-full bg-aera-lightgray border border-aera-lightgray/80 pointer-events-none cursor-wait"
                aria-busy="true"
                role="status"
              >
                <Loader2
                  className="w-6 h-6 text-aera-gray animate-spin"
                  aria-hidden
                />
                <span className="sr-only">Loading session</span>
              </div>
            ) : isLoggedIn ? (
              <div className="">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <AeraButton
                      variant="outline"
                      paddingSize="none"
                      className="w-10 h-10 overflow-hidden rounded-full!"
                    >
                      <AeraImage
                        native
                        src={userAvatar}
                        alt="Profile"
                        referrerPolicy="no-referrer"
                        width={40}
                        height={40}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://ui-avatars.com/api/?name=User&background=random";
                        }}
                      />
                    </AeraButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    alignOffset={20}
                    sideOffset={8}
                    className="w-auto bg-aera-white rounded-lg shadow-xl border border-aera-lightgray/80 py-2 ml-2"
                  >
                    {userDisplayName ? (
                      <>
                        <div className="px-4 pt-2 pb-1">
                          <div className="text-xs text-aera-slate/70">
                            {userDisplayName}
                          </div>
                        </div>
                        <div className="my-1 h-px bg-aera-lightgray/80" />
                      </>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => router.push("/profile")}
                      className="w-full px-4 py-2 text-left text-sm text-aera-slate hover:bg-aera-offwhite transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <div className="text-nowrap">ดูโปรไฟล์</div>
                      <User className="w-4 h-4" strokeWidth={2} />
                    </DropdownMenuItem>
                    <div className="my-1 h-px bg-aera-lightgray/80" />
                    <DropdownMenuItem
                      onClick={() =>
                        router.push("https://hireaera.com/terms-of-use")
                      }
                      className="w-full px-4 py-2 text-left text-sm text-aera-slate hover:bg-aera-offwhite transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <div className="text-nowrap">ข้อตกลงใช้งาน</div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        router.push("https://hireaera.com/privacy")
                      }
                      className="w-full px-4 py-2 text-left text-sm text-aera-slate hover:bg-aera-offwhite transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <div className="text-nowrap">นโยบายความเป็นส่วนตัว</div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => openCookieConsentBanner()}
                      className="w-full px-4 py-2 text-left text-sm text-aera-slate hover:bg-aera-offwhite transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <div className="text-nowrap">จัดการคุกกี้</div>
                    </DropdownMenuItem>
                    <div className="my-1 h-px bg-aera-lightgray/80" />
                    <DropdownMenuItem
                      onClick={handleLogoutAction}
                      className="w-full px-4 py-2 text-left text-sm text-aera-slate hover:bg-aera-offwhite transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <div className="text-nowrap">ออกจากระบบ</div>

                      <LogOut className="w-4 h-4" strokeWidth={2} />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="relative">
                <AeraButton
                  variant="secondary"
                  type="button"
                  paddingSize="none"
                  disabled={isLoginButtonDisabled}
                  aria-disabled={isLoginButtonDisabled}
                  onClick={() => {
                    if (isLoginButtonDisabled) return;
                    setIsLoginNavigationPending(true);
                    navigateToSignin(router, "/signin");
                  }}
                  className={`w-10 h-10 rounded-full!`}
                >
                  <LogIn
                    size={14}
                    className="w-7 h-7 p-0.75 ps-0"
                    strokeWidth={2.5}
                  />
                </AeraButton>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Merged Service Index / Discover Content --- */}
      <div
        className={`h-[calc(100dvh-0px)] w-full min-w-0 flex flex-col md:flex-row relative overflow-hidden ${mobileFullWidthPanel ? "max-md:z-40 ms-16 md:ms-0" : "z-0 ms-16 md:ms-0"}`}
      >
        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder={"ค้นหาบริการ..."}
          className="min-h-14 flex md:hidden"
          onHowItWorksClick={() => setActiveModal("HOW_IT_WORKS")}
        />

        {/* Sidebar — Hire Aera nav hidden on mobile; content shows directly */}
        <div
          className={`mt-0 md:mt-14 rounded-tl-3xl inset-y-0 left-0 z-0 border-r-0 border-aera-lightgray transition-transform duration-300 ease-in-out relative translate-x-0 ${hideHireSidebarOnMobile ? "hidden md:block" : ""}`}
          style={{
            width:
              mounted && windowWidth >= 768
                ? `${effectiveSidebarWidth}px`
                : "auto",
          }}
        >
          <div className="h-[calc(100dvh-56px)] md:h-full overflow-hidden rounded-tl-0 md:rounded-tl-3xl bg-aera-white rounded-tl-3xl border border-b-0 border-aera-lightgray/80">
            {showDiscover ? (
              <>
                {showDiscoverSidebarSkeleton ? (
                  <DiscoverSidebarSkeleton />
                ) : mounted && !isLoadingSession && isLoggedIn ? (
                  <>
                    {/* ค้นหาบริการ sidebar — temporarily hidden
                    <div className="p-4 lg:p-6 h-full overflow-y-auto">
                      <h1 className="text-2xl font-bold text-aera-charcoal mb-8 px-2">
                        ค้นหาบริการ
                      </h1>
                      <nav className="space-y-1">
                        <div
                          onClick={() => {
                            if (window.innerWidth < 768) handleMenuClick(true);
                          }}
                          className="flex items-center gap-3 px-3 py-2 bg-aera-lightgray rounded-lg text-aera-charcoal font-semibold cursor-pointer md:cursor-default"
                        >
                          <div className="w-2.5 h-2.5 bg-aera-charcoal rounded-full"></div>
                          <span>AI Workers</span>
                        </div>
                      </nav>
                    </div>
                    */}
                  </>
                ) : (
                  <>
                    <div className="p-4 lg:p-6 h-full overflow-y-auto">
                      <h1 className="text-2xl font-bold text-aera-charcoal mb-8 px-2">
                        Hireaera
                      </h1>
                      <nav className="space-y-1">
                        {HIRE_AERA_SECTION_NAV.map(({ id, label }) => (
                          <AeraButton
                            key={id}
                            variant="transparent"
                            size="sm"
                            fullWidth
                            paddingSize="md"
                            onClick={() => scrollToHireSection(id)}
                            className="justify-start! hover:bg-aera-lightgray!"
                          >
                            <span className="w-2.5 h-2.5 shrink-0 rounded-full bg-aera-gray" />
                            <span className="text-nowrap">{label}</span>
                          </AeraButton>
                        ))}
                      </nav>
                    </div>
                  </>
                )}
              </>
            ) : (
              <WorkerSidebar
                selectedWorker={selectedWorker}
                setSelectedWorker={(worker, source) => {
                  const workerIsHired = hiredWorkerIds.includes(worker.id);

                  // Helper to push updates
                  const updateUrl = (p: URLSearchParams) => {
                    router.push(`/?${p.toString()}`, { scroll: false });
                  };

                  if (source === "marketplace") {
                    const params = new URLSearchParams();
                    if (currentWorkspaceId)
                      params.set("workspaceId", currentWorkspaceId);
                    params.set("serviceId", worker.id);
                    params.set("source", "marketplace");
                    updateUrl(params);
                  } else if (source === "my-team") {
                    const params = new URLSearchParams();
                    if (worker.workspaceId)
                      params.set("workspaceId", worker.workspaceId);
                    else if (currentWorkspaceId)
                      params.set("workspaceId", currentWorkspaceId);
                    params.set("serviceId", worker.id);
                    params.set("source", "order");
                    updateUrl(params);
                  } else {
                    if (workerIsHired) {
                      const params = new URLSearchParams();
                      if (worker.workspaceId)
                        params.set("workspaceId", worker.workspaceId);
                      else if (currentWorkspaceId)
                        params.set("workspaceId", currentWorkspaceId);
                      params.set("serviceId", worker.id);
                      params.set("source", "order");
                      updateUrl(params);
                    } else {
                      const params = new URLSearchParams();
                      if (currentWorkspaceId)
                        params.set("workspaceId", currentWorkspaceId);
                      params.set("serviceId", worker.id);
                      params.set("source", "marketplace");
                      updateUrl(params);
                    }
                  }
                  setShowMobileSidebar(false);
                }}
                hiredWorkerIds={hiredWorkerIds}
                apiWorkers={apiWorkers}
                isLoadingWorkers={isLoadingWorkers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                width={effectiveSidebarWidth}
                selectedServiceId={serviceId || ""}
                suppressLoading={false}
                isWorkspaceLoading={isSidebarWorkspaceLoading}
                activeTab={activeTab}
                workspaces={workspaces}
                currentWorkspaceId={currentWorkspaceId}
                isLoadingWorkspaces={isLoadingWorkspaces}
                onAddWorkspace={openCreateWorkspaceModal}
                onOpenWorkspaceModal={openWorkspaceSettingsModal}
                onWorkspaceUpdated={fetchWorkspaces}
              />
            )}
          </div>
        </div>

        {/* Sidebar Resize Handle - hidden on mobile */}
        <div
          className={`hidden lg:block w-px h-[calc(100dvh-56px)] mt-14 relative z-60 group cursor-col-resize hover:bg-aera-lightgray transition-colors ${isResizingSidebar ? "bg-aera-amberlight" : "bg-transparent"}`}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizingSidebar(true);
          }}
        >
          {/* Enlarged Hit Area */}
          <div className="absolute inset-y-0 -left-2 w-4 z-50 bg-transparent cursor-col-resize" />

          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-8 bg-aera-white border border-aera-lightgray/80 shadow-sm rounded-full flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="w-1 h-1 bg-aera-gray rounded-full" />
            <div className="w-1 h-1 bg-aera-gray rounded-full" />
            <div className="w-1 h-1 bg-aera-gray rounded-full" />
          </div>
        </div>

        {/* Main Content Area */}
        <div
          ref={mainPanelRef}
          data-main-panel-width={mainPanelWidth}
          className={`${
            mobileMainPanelOpen ? "translate-x-0" : "translate-x-full"
          } md:translate-x-0 transition-transform ease-in-out duration-300 fixed z-50 ${
            mobileFullWidthPanel
              ? "left-0 w-full"
              : "left-16 w-[calc(100%-4rem)]"
          } md:left-auto md:relative md:w-full min-w-0 flex-1 flex flex-col h-[calc(100dvh-0px)] bg-aera-white ${showServiceView && !serviceId ? "hidden md:flex" : ""}`}
        >
          <SearchInput
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder={"ค้นหาบริการ..."}
            className="h-14 flex"
            onHowItWorksClick={() => setActiveModal("HOW_IT_WORKS")}
          />

          {/* {showDiscover ? ( */}
          {showServiceView ? (
            <>
              {serviceId ? (
                // Render service detail - adjust height to account for search input
                <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col h-[calc(100dvh-56px)]">
                  <div
                    key={serviceId}
                    className="h-full min-h-0 w-full min-w-0"
                  >
                    <ServiceDetailInline
                      serviceId={serviceId}
                      containerWidth={mainPanelWidth}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-8 border-t border-aera-lightgray">
                  <div className="h-12.5 flex md:hidden items-center justify-start mb-0">
                    <div
                      onClick={() => {
                        if (window.innerWidth < 768) handleMenuClick(false);
                      }}
                      className="p-2 text-aera-charcoal mb-0 flex "
                    >
                      <ArrowLeft size={20} />
                    </div>
                  </div>

                  <div className="flex items-center justify-center h-[calc(100dvh-170px)] md:h-full text-aera-gray">
                    กรุณาเลือกบริการที่ต้องการใช้
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 overflow-y-auto px-4 py-4 md:p-8 space-y-6 md:space-y-8 pb-20 border-t border-l md:border-l-0 border-aera-lightgray">
              {!hideHireSidebarOnMobile && (
                <div
                  onClick={() => {
                    if (window.innerWidth < 768) handleMenuClick(false);
                  }}
                  className="p-2 -ml-2 text-aera-charcoal block md:hidden"
                >
                  <ArrowLeft size={24} />
                </div>
              )}

              <DiscoverContent
                searchQuery={searchQuery}
                isDiscover={showDiscover}
                onReadyChange={setIsDiscoverContentReady}
              />
            </div>
          )}

          {/* Logged-out footer (bottom links) */}
          {!isLoadingSession && !isLoggedIn && (
            <div className="shrink-0 border-t border-l md:border-l-0 border-aera-lightgray bg-aera-white/95 backdrop-blur">
              <div className="mx-auto max-w-6xl px-4 py-3 flex flex-wrap items-center justify-center gap-y-2 gap-x-3 md:gap-x-6 text-xs md:text-sm text-aera-slate">
                <span className="w-full lg:w-auto text-center lg:text-left">
                  Copyright © 2025{" "}
                  <AeraButton
                    as="a"
                    href=""
                    variant="link"
                    size="xs"
                    className="inline text-aera-amber hover:text-aera-charcoal"
                  >
                    HireAera.com
                  </AeraButton>{" "}
                  All rights reserved.
                </span>

                <AeraButton
                  as="a"
                  href="https://hireaera.com/privacy"
                  variant="link"
                  size="xs"
                  className="text-aera-amber hover:text-aera-charcoal"
                >
                  นโยบายความเป็นส่วนตัว
                </AeraButton>
                <AeraButton
                  as="a"
                  href="https://hireaera.com/terms-of-use"
                  variant="link"
                  size="xs"
                  className="text-aera-amber hover:text-aera-charcoal"
                >
                  เงื่อนไขการใช้งาน
                </AeraButton>
                <AeraButton
                  variant="link"
                  size="xs"
                  type="button"
                  onClick={() => openCookieConsentBanner()}
                  className="text-aera-amber hover:text-aera-charcoal"
                >
                  จัดการคุกกี้
                </AeraButton>
              </div>
            </div>
          )}
        </div>
      </div>

      <WorkspaceListModal
        isOpen={isWorkspaceModalOpen}
        mode={workspaceModalMode}
        initialWorkspaceId={
          workspaceModalMode === "edit" ? currentWorkspaceId : null
        }
        onClose={closeWorkspaceModal}
        onSuccess={(workspaceId) => {
          fetchWorkspaces();
          setIsWorkspaceModalOpen(false);
          patchUrlSearchParams((params) => {
            params.delete("setting");
            if (workspaceModalMode === "create") {
              params.delete("serviceId");
              params.delete("source");
            }
            if (workspaceId) params.set("workspaceId", workspaceId);
          });
        }}
      />

      <WorkspaceSelectModal
        isOpen={isWorkspaceSelectModalOpen}
        onClose={dismissWorkspaceSelectModal}
        workspaces={workspaces}
        selectedWorkspaceId={workspacePickHintId ?? currentWorkspaceId}
        onSelect={handleWorkspaceSelectFromModal}
        isLoading={isLoadingWorkspaces}
        isSubmitting={isWorkspacePickSubmitting}
      />

      {/* --- Unified Modal Handling --- */}
      {/* Special case: Auto-submitting Setup (Business Setup) should be invisible */}
      {activeModal === "SETUP" && modalProps.autoSubmit && (
        <div className="hidden">
          <DynamicSetupModal
            isOpen={true}
            onClose={handleModalClose}
            onSubmit={handleRemoteSetupSubmit}
            config={modalProps.config}
            title={modalProps.title}
            serviceId={modalProps.serviceId}
            stepTitle={modalProps.stepTitle}
            initialValues={modalProps.initialValues}
            autoSubmit={modalProps.autoSubmit}
            isEmbeddedInModal={true}
          />
        </div>
      )}

      {displayedModal === "SHARE" && (
        <ShareModal
          isOpen={activeModal === "SHARE"}
          onClose={handleModalClose}
          onExited={onActiveModalExited}
          shareUrl={modalProps.shareUrl}
          title={modalProps.title}
        />
      )}

      {displayedModal === "HIRE" && (
        <HireModal
          isOpen={activeModal === "HIRE"}
          onClose={handleModalClose}
          onExited={onActiveModalExited}
          onConfirm={async (orderId, workspaceId, workspaceName) => {
            handleModalClose();

            if (workspaceName) {
              sessionStorage.setItem(
                "aera_setup_workspace_name",
                workspaceName,
              );
            }

            if (orderId) {
              const targetWorkspaceId =
                workspaceId || modalProps.workspaceId || currentWorkspaceId;

              await fetchOrders("buyer", targetWorkspaceId || undefined);

              const params = new URLSearchParams();
              if (targetWorkspaceId)
                params.set("workspaceId", targetWorkspaceId);

              if (modalProps.serviceId)
                params.set("serviceId", modalProps.serviceId);
              params.set("source", "order");

              router.push(`/?${params.toString()}`);
              return;
            }

            if (modalProps.serviceId && modalProps.packageId) {
              addToCart({
                serviceId: modalProps.serviceId,
                packageId: modalProps.packageId,
                title: modalProps.workerData?.title || "AI Worker Service",
                price: parseFloat(
                  modalProps.workerData?.price?.replace(/[^0-9.]/g, "") || "0",
                ),
                priceDisplay: modalProps.workerData?.price || "$0.00",
                capacity: modalProps.workerData?.capacity || "0",
              });
            }
          }}
          workerData={modalProps.workerData}
          serviceId={modalProps.serviceId}
          packageId={modalProps.packageId}
          workspaceId={modalProps.workspaceId || currentWorkspaceId}
          onWorkspaceCreated={() => fetchWorkspaces()}
        />
      )}

      {/* Normal Modals (shell กลาง — ไม่ห่อ HIRE/SHARE ที่มี Modal ของตัวเอง) */}
      {displayedModal &&
        displayedModal !== "HIRE" &&
        displayedModal !== "SHARE" &&
        (displayedModal !== "SETUP" || !modalProps.autoSubmit) && (
          <Modal
            isOpen={isActiveModalOpen}
            onClose={handleModalClose}
            onExited={onActiveModalExited}
            title={
              displayedModal === "SETUP" ? (
                modalProps.title || "Setup"
              ) : displayedModal === "RESPONSE" ? (
                modalProps.success ? (
                  "Success"
                ) : (
                  "Notice"
                )
              ) : displayedModal === "PREVIEW" ? (
                modalProps.file?.name || "File Preview"
              ) : displayedModal === "HOW_IT_WORKS" ? (
                <>วิธีการทำงาน</>
              ) : (
                ""
              )
            }
            // Footer logic can be moved here or handled within specific components if desired.
            // For now, simpler to let specific components render their own footers/actions inside body
            // OR pass null if the generic Modal handles it.
            // Given DynamicSetupModal/ResponseModal handle their own footers, we might pass null here
            // and let them handle layout, BUT the User asked for "header, footer modal".
            // So ideally we map the actions here.

            footer={
              displayedModal === "SETUP" ? (
                <>
                  <AeraButton
                    type="button"
                    variant="outline"
                    fontWeight="medium"
                    className="flex-1"
                    onClick={handleModalClose}
                  >
                    ยกเลิก
                  </AeraButton>
                  <AeraButton
                    type="submit"
                    form="dynamic-setup-form"
                    variant="primary"
                    fontWeight="medium"
                    className="flex-1"
                    loading={setupSubmitting}
                  >
                    {setupSubmitting ? "กำลังบันทึก..." : "บันทึกการตั้งค่า"}
                  </AeraButton>
                </>
              ) : displayedModal === "HOW_IT_WORKS" ? (
                <div className="w-full flex justify-center">
                  <AeraButton
                    variant="primary"
                    size="lg"
                    paddingSize="xl"
                    fontWeight="medium"
                    onClick={() => {
                      const key = userId
                        ? `aera_last_how_it_works_seen_${userId}`
                        : "aera_last_how_it_works_seen_public";
                      localStorage.setItem(key, Date.now().toString());
                      handleModalClose();
                    }}
                  >
                    เริ่มใช้งาน
                  </AeraButton>
                </div>
              ) : displayedModal === "PREVIEW" ? (
                <AeraButton
                  type="button"
                  variant="outline"
                  size="sm"
                  fontWeight="medium"
                  onClick={handleModalClose}
                >
                  ปิด
                </AeraButton>
              ) : null
            }
            size={
              displayedModal === "PREVIEW"
                ? (() => {
                    const fileType = modalProps.file?.type || "";
                    const isImage =
                      fileType.includes("image") ||
                      ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(
                        fileType.toLowerCase(),
                      );
                    return isImage ? "2xl" : "full";
                  })()
                : displayedModal === "HOW_IT_WORKS"
                  ? "3xl" // increase size for better spacing
                  : "lg"
            }
            hideCloseButton={displayedModal === "HOW_IT_WORKS"}
            preventBackdropClose={
              displayedModal === "HOW_IT_WORKS" || setupSubmitting
            }
            closeDisabled={setupSubmitting}
          >
            {displayedModal === "SETUP" && (
              <DynamicSetupModal
                isOpen={true}
                onClose={handleModalClose}
                onSubmit={handleRemoteSetupSubmit}
                config={modalProps.config}
                title={modalProps.title}
                serviceId={modalProps.serviceId}
                stepTitle={modalProps.stepTitle}
                initialValues={modalProps.initialValues}
                autoSubmit={modalProps.autoSubmit}
                isEmbeddedInModal={true}
                onSubmittingChange={setSetupSubmitting}
              />
            )}
            {displayedModal === "RESPONSE" && (
              <ResponseModal
                isOpen={true}
                onClose={handleModalClose}
                response={modalProps.response}
                success={modalProps.success}
                onReloadChat={handleRemoteReloadChat}
                isEmbeddedInModal={true}
              />
            )}
            {displayedModal === "PREVIEW" && (
              <FilePreviewModal
                file={modalProps.file}
                onClose={handleModalClose}
                isEmbeddedInModal={true}
              />
            )}
            {displayedModal === "HOW_IT_WORKS" && (
              <div className="space-y-6">
                <HowAeraWorksSection
                  className="w-full grid gap-4"
                  showTitleIcon={false}
                  showCta={false}
                />
              </div>
            )}
          </Modal>
        )}
    </div>
  );
}

// Disable static generation for routes using searchParams
export const dynamic = "force-dynamic";

export default function Home() {
  return <HomeContent />;
}
